// context/ReportProfileContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { getPersistedReportId, hydrateReportId } from '../system/report-id-storage';
import { normalizeReportProfileVisibility } from '../system/report-visibility';

const ReportProfileContext = createContext();

export const ReportProfileProvider = ({ children, appVars }) => {
  const [reportProfile, setReportProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appMenu, setappMenu] = useState();


  useEffect(() => {
    let didCancel = false;

    const waitForRIDAndFetch = async () => {
      let tries = 0;
      let rid = hydrateReportId() || getPersistedReportId();
      while (!rid && tries < 20 && !didCancel) {
        await new Promise(res => setTimeout(res, 100)); // wait 100ms
        tries++;
        rid = hydrateReportId() || getPersistedReportId();
      }
      if (!rid) {
        setLoading(false);
        console.warn("RID was never persisted for this report.");
        return;
      }

      try {
        const res = await fetch(`${appVars.api_url}/reportprofile/${rid}`);
        const data = normalizeReportProfileVisibility(await res.json());
        const parsedTabs = data.tabs ? JSON.parse(data.tabs) : null;
        const profileTabs = parsedTabs?.tabs ?? parsedTabs;
        const profileSettings = data.settings ? JSON.parse(data.settings) : null;
        const adSets = profileSettings?.ad_sets;
       // console.log('Profile Tabs', profileTabs);
        // console.log('Profile Ad Sets', adSets);

        function normalizeSegment(url) {
          if (!url) return '';
          const cleaned = String(url).trim().replace(/\\/g, '/').replace(/\/+/g, '/');
          return cleaned.replace(/^\/+/, '').replace(/\/+$/, '').replace(/^mr\/infillion$/, 'mr/programmatic');
        }

        function normalizeProgrammaticTitle(title, segment) {
          const rawTitle = String(title || '').trim();
          const normalizedTitle = rawTitle.toLowerCase();
          const normalizedSegment = String(segment || '').trim().toLowerCase();

          if (
            normalizedTitle === 'display retail' ||
            normalizedTitle === 'stackadapt retail' ||
            normalizedSegment === 'display/retail'
          ) {
            return 'Programmatic Retail';
          }

          if (
            normalizedTitle === 'display' ||
            normalizedTitle === 'stackadapt' ||
            normalizedTitle === 'infillion' ||
            normalizedSegment === 'mr/display' ||
            normalizedSegment === 'vimarc/mr/display'
          ) {
            return 'Programmatic';
          }

          return rawTitle;
        }

        function getIconKey(tabKey, label, segment) {
          const haystack = `${tabKey} ${label} ${segment}`.toLowerCase();
          if (haystack.includes('reportwerks')) return 'reportwerks';
          if (haystack.includes('instant_analysis') && haystack.includes('analysis')) return 'reportwerks';
          if (haystack.includes('journey')) return 'reportwerks';
          if (haystack.includes('donation')) return 'donations';
          if (haystack.includes('chatgpt')) return 'chatgpt_ads';
          if (haystack.includes('google')) return 'google';
          if (haystack.includes('meta')) return 'meta';
          if (haystack.includes('bing')) return 'bing';
          if (haystack.includes('stackadapt') || haystack.includes('display')) return 'display';
          if (haystack.includes('infillion') || haystack.includes('programmatic')) return 'display';
          if (haystack.includes('linkedin')) return 'linkedin';
          if (haystack.includes('mautic')) return 'mautic';
          if (haystack.includes('mailchimp')) return 'mailchimp';
          if (haystack.includes('delivera') || haystack.includes('delivra')) return 'delivera';
          if (haystack.includes('constant contact')) return 'constantcontact';
          if (haystack.includes('salsa')) return 'salsalabs';
          if (haystack.includes('virtuous')) return 'virtuous';
          if (haystack.includes('email')) return 'email';
          if (haystack.includes('lightbox')) return 'lightbox';
          if (haystack.includes('matomo')) return 'matomo';
          if (haystack.includes('analytics')) return 'analytics';
          if (haystack.includes('overview') || haystack.includes('dashboard')) return 'dashboard';
          return 'dashboard';
        }

        function normalizeKey(value) {
          return String(value || '')
            .toLowerCase()
            .replace(/\\/g, '/')
            .replace(/[^a-z0-9/]+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '');
        }

        function buildAllowedTabSet(settings, tabs) {
          const allow = new Set();
          if (!settings || typeof settings !== 'object') {
            const tabEntries = tabs && typeof tabs === 'object' ? Object.entries(tabs) : [];
            tabEntries.forEach(([key, value]) => {
              const label = value?.tablabel || value?.label || key;
              allow.add(normalizeKey(key));
              allow.add(normalizeKey(label));
            });
            return allow.size ? allow : null;
          }
          Object.entries(settings).forEach(([key, value]) => {
            const id = value?.id;
            if (!id || String(id).length <= 0) return;
            const label = value?.tablabel || value?.label || key;
            allow.add(normalizeKey(key));
            allow.add(normalizeKey(label));
          });
          const tabEntries = tabs && typeof tabs === 'object' ? Object.entries(tabs) : [];
          tabEntries.forEach(([key, value]) => {
            const label = value?.tablabel || value?.label || key;
            allow.add(normalizeKey(key));
            allow.add(normalizeKey(label));
          });
          return allow;
        }

        function buildNavigation(tabs, allowedSet) {
          if (!tabs || typeof tabs !== 'object') return [];
          return Object.entries(tabs).reduce((navItems, [key, value], index) => {
            if (!value) return navItems;

            const segment = normalizeSegment(value?.url || value?.path || value?.to);
            if (!segment) return navItems;

            const rawTitle = value?.label || value?.title || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const title = normalizeProgrammaticTitle(rawTitle, segment);
            const icon = value?.icon || getIconKey(key, title, segment);
            const allowed =
              !allowedSet ||
              allowedSet.size === 0 ||
              allowedSet.has(normalizeKey(key)) ||
              allowedSet.has(normalizeKey(title));

            if (!allowed) return navItems;

            navItems.push({
              key,
              to: segment,
              title,
              segment,
              icon,
              order: index
            });

            return navItems;
          }, []);
        }

        let navigation;
        if (Array.isArray(data.reportNavigation)) {
          navigation = data.reportNavigation.map((report, order) => ({
            key: report.key, to: report.url, segment: report.url, rendererRoute: report.rendererRoute, title: data.reportNavigationMode === 'assigned' ? report.label : normalizeProgrammaticTitle(report.label, report.url),
            icon: report.icon || getIconKey(report.key, report.label, report.url), order,
          }));
        } else {
        let linkedinConnected = false;
        try {
          const linkedinStatusResponse = await fetch(`${appVars.api_url}/linkedin/token-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cid: rid })
          });
          const linkedinStatus = await linkedinStatusResponse.json();
          linkedinConnected = linkedinStatusResponse.ok && linkedinStatus?.status === 'valid';
        } catch (linkedinStatusError) {
          console.warn('Unable to confirm LinkedIn connection status:', linkedinStatusError);
        }

        const allowedTabs = buildAllowedTabSet(adSets, profileTabs);
      //  console.log('Allowed Tabs:', adSets);
        navigation = buildNavigation(profileTabs, allowedTabs).filter((item) => (
          getIconKey('', item.title, item.segment) !== 'linkedin' || linkedinConnected
        ));
        if (linkedinConnected && !navigation.some((item) => item.segment === 'mr/linkedin')) {
          navigation.push({
            to: 'mr/linkedin',
            title: 'LinkedIn',
            segment: 'mr/linkedin',
            icon: 'linkedin',
            order: 53
          });
        }
        if ((adSets?.delivra?.id || adSets?.delivera?.id) && !navigation.some((item) => item.segment === 'mr/delivera')) {
          navigation.push({
            to: 'mr/delivera',
            title: 'Delivra',
            segment: 'mr/delivera',
            icon: "delivera",
            order: 54
          });
        }
        if (adSets?.chatgpt_ads?.id && !navigation.some(item => item.segment === 'mr/chatgpt-ads')) {
          navigation.push({ to: 'mr/chatgpt-ads', title: 'ChatGPT Ads', segment: 'mr/chatgpt-ads', icon: 'chatgpt_ads', order: 55 });
        }
        if ((adSets?.infillion?.advertiser_ids?.length || adSets?.infillion?.id?.length) && !navigation.some((item) => item.segment === 'mr/programmatic')) {
          navigation.push({
            to: 'mr/programmatic',
            title: 'Programmatic',
            segment: 'mr/programmatic',
            icon: 'display',
            order: 55
          });
        }
        let matomoConnected = false;
        try {
          const response = await fetch(`${appVars.api_url}/matomo/token-status`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cid: rid }),
          });
          matomoConnected = response.ok && (await response.json())?.status === 'valid';
        } catch (error) { console.warn('Unable to confirm Matomo connection status:', error); }
        navigation = navigation.filter((item) => getIconKey('', item.title, item.segment) !== 'matomo' || matomoConnected);
        const matomoSiteId = String(adSets?.matomo?.id || data.beaconId || '').trim();
        if (matomoConnected && matomoSiteId && matomoSiteId !== '0' && !navigation.some((item) => item.segment === 'mr/matomo')) {
          navigation.push({ to: 'mr/matomo', title: 'Matomo', segment: 'mr/matomo', icon: 'matomo', order: 56 });
        }
        }
        if (data.reportNavigationMode !== 'assigned' && !navigation.some((item) => item.segment === 'mr/notes')) navigation.push({
          to: 'mr/notes',
          title: 'Notes',
          segment: 'mr/notes',
          icon: "notes",
          order: 99
        });
        navigation.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

        if (didCancel) return;
        setappMenu(navigation);
        setReportProfile(data);

      } catch (err) {
        console.error('Failed to load report profile:', err);
      } finally {
        setLoading(false);
      }
    };

    if (appVars?.api_url) {
      waitForRIDAndFetch();
    }

    return () => { didCancel = true; };
  }, [appVars]);

  return (
    <ReportProfileContext.Provider value={{ reportProfile, setReportProfile, loading, appMenu }}>
      {children}
    </ReportProfileContext.Provider>
  );
};

export const useReportProfile = () => useContext(ReportProfileContext);
